import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';
import { useConsoleTheme } from '../../lib/theme';

const BAR_COLOR = '#B0FC38';

// Recharts takes colours as props, so the theme is chosen here
// (docs/light-mode.md §3). Dark values are the originals, unchanged. Light:
// bars in the accent (#4D7C0F, non-text), value ticks ink-3, category labels
// ink-2, cursor an accent tint.
const CHART = {
  dark: {
    bar: BAR_COLOR,
    axis: 'rgba(255,255,255,0.2)',
    valueTick: 'rgba(255,255,255,0.35)',
    labelTick: 'rgba(255,255,255,0.65)',
    cursor: 'rgba(176,252,56,0.06)',
  },
  light: {
    bar: '#4D7C0F',
    axis: 'rgba(28,27,22,0.3)',
    valueTick: '#5C584C',
    labelTick: '#4A473D',
    cursor: 'rgba(77,124,15,0.1)',
  },
};
const ROW_HEIGHT = 32;
const MIN_HEIGHT = 220;

function truncate(value, max = 28) {
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function PathTooltip({ active, payload, siteUrl }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const href = (() => {
    if (!siteUrl) return null;
    try { return new URL(row.path, siteUrl).toString(); } catch { return null; }
  })();
  return (
    <div className="px-3 py-2.5 rounded-lg bg-[#0a0a0f] border border-white/[0.08] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] max-w-[320px]">
      <div className="text-[11px] text-gray-500 tracking-wider uppercase mb-1">Path</div>
      <div className="text-[12px] text-white font-mono break-all">{row.path}</div>
      <div className="mt-2 text-[11px] text-gray-500">
        <span className="text-[#B0FC38] font-medium tabular-nums">{row.views.toLocaleString()}</span> views in the last 24 hours.
      </div>
      {href && (
        <div className="mt-1 text-[10px] text-gray-600 truncate">{href}</div>
      )}
    </div>
  );
}

export default function TopPages({ pages, siteUrl, loading, window: dataWindow }) {
  const { isLight } = useConsoleTheme();
  const c = isLight ? CHART.light : CHART.dark;
  const subtitle = dataWindow === 'unavailable'
    ? 'Top-paths data is temporarily unavailable.'
    : 'Most visited paths in the last 24 hours.';

  if (loading) {
    return (
      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-5 h-full">
        <div className="mb-4">
          <h3 className="text-sm font-medium text-white tracking-tight">Top pages</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex flex-col gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 rounded bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const hasData = Array.isArray(pages) && pages.length > 0;
  const chartHeight = hasData ? Math.max(MIN_HEIGHT, pages.length * ROW_HEIGHT + 40) : MIN_HEIGHT;

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-5 h-full flex flex-col">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-white tracking-tight">Top pages</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="text-sm text-gray-500">No page data in this window.</div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={pages}
            layout="vertical"
            margin={{ top: 4, right: 24, left: 0, bottom: 4 }}
            barCategoryGap={6}
          >
            <XAxis
              type="number"
              stroke={c.axis}
              tick={{ fontSize: 10, fill: c.valueTick }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="path"
              stroke={c.axis}
              tick={{ fontSize: 11, fill: c.labelTick, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
              axisLine={false}
              tickLine={false}
              width={150}
              interval={0}
              tickFormatter={(v) => truncate(v, 22)}
            />
            <Tooltip
              content={<PathTooltip siteUrl={siteUrl} />}
              cursor={{ fill: c.cursor }}
            />
            <Bar dataKey="views" radius={[0, 4, 4, 0]}>
              {pages.map((entry, i) => (
                <Cell key={entry.path} fill={c.bar} fillOpacity={1 - i * 0.06} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
